/**
 * Numeric helpers tuned for the sloppiness real LandXML writers produce:
 * `0.` and `.5` trailing/leading dots, `1E-05`, XML-schema `INF`/`-INF`,
 * empty attributes, and newline-wrapped numeric text content.
 */

export function num(s: string | undefined | null): number | undefined {
  if (s == null) return undefined;
  const t = s.trim();
  if (t === '') return undefined;
  if (t === 'INF' || t === '+INF' || t === 'Infinity') return Infinity;
  if (t === '-INF' || t === '-Infinity') return -Infinity;
  const v = Number(t);
  return Number.isNaN(v) ? undefined : v;
}

export function numOr(s: string | undefined | null, fallback: number): number {
  const v = num(s);
  return v === undefined ? fallback : v;
}

/** Detect comma decimal separators from mis-localised exporters. */
export function looksCommaDecimal(s: string): boolean {
  return /\d,\d/.test(s) && !/\d\s+\d/.test(s.replace(/,/g, ''));
}

/**
 * Parse whitespace-delimited doubles out of element text content without
 * allocating an intermediate string array. Handles `\n`, `\r`, `\t` and runs of
 * spaces, which is how multi-megabyte `<Faces>` and `<PntList2D>` blocks arrive.
 */
export function parseDoubles(text: string, out?: number[]): number[] {
  const res = out ?? [];
  const n = text.length;
  let i = 0;
  while (i < n) {
    while (i < n && isWs(text.charCodeAt(i))) i++;
    if (i >= n) break;
    const start = i;
    while (i < n && !isWs(text.charCodeAt(i))) i++;
    const tok = text.slice(start, i);
    if (tok === 'INF' || tok === '+INF') { res.push(Infinity); continue; }
    if (tok === '-INF') { res.push(-Infinity); continue; }
    const v = Number(tok);
    if (!Number.isNaN(v)) res.push(v);
  }
  return res;
}

function isWs(c: number): boolean {
  return c === 32 || c === 10 || c === 13 || c === 9 || c === 12;
}

/** Parse whitespace-delimited tokens (CgPoint name lists, face neighbour lists). */
export function parseTokens(text: string): string[] {
  const t = text.trim();
  return t === '' ? [] : t.split(/\s+/);
}
